import { signal } from "@preact/signals";
import type { FunctionComponent } from "preact";
import { useRef, useState } from "preact/hooks";
import { resetKeybinds, resetSection } from "../../settings";
import type { SettingsSection } from "../../settings";
import { toast } from "../../state/toasts";
import { Button } from "../components/Button";
import { Modal } from "../components/Modal";
import { ScreenFrame } from "../components/ScreenFrame";
import { Tabs, panelId, tabId } from "../components/Tabs";
import { useArrowNav } from "../hooks/useArrowNav";
import { AudioTab } from "./settings/AudioTab";
import { ControlsTab } from "./settings/ControlsTab";
import { CrosshairTab } from "./settings/CrosshairTab";
import { HudTab } from "./settings/HudTab";
import { KeybindsTab } from "./settings/KeybindsTab";
import { NetworkTab } from "./settings/NetworkTab";
import { VideoTab } from "./settings/VideoTab";

interface TabSpec {
  id: string;
  label: string;
  /** One line under the tabs. */
  lede: string;
  Content: FunctionComponent;
  /** Section restored by "Reset this tab"; null for the keybinds, which confirm first. */
  section: SettingsSection | null;
}

const TABS = [
  { id: "controls", label: "Controls", lede: "Mouse sensitivity and input behavior.", Content: ControlsTab, section: "controls" },
  { id: "keybinds", label: "Keybinds", lede: "Up to two keys, mouse buttons or wheel directions per action.", Content: KeybindsTab, section: null },
  { id: "video", label: "Video", lede: "Field of view, rendering quality and effects.", Content: VideoTab, section: "video" },
  { id: "crosshair", label: "Crosshair", lede: "Shape, color and outline, with a live preview.", Content: CrosshairTab, section: "crosshair" },
  { id: "hud", label: "HUD", lede: "Size of the interface and which readouts to show.", Content: HudTab, section: "hud" },
  { id: "audio", label: "Audio", lede: "Volumes and combat sounds.", Content: AudioTab, section: "audio" },
  { id: "network", label: "Network", lede: "Connection transport and remote player smoothing.", Content: NetworkTab, section: "network" },
] as const satisfies readonly TabSpec[];

type TabId = (typeof TABS)[number]["id"];

const ID_PREFIX = "settings";

/** Remembered across visits so reopening the screen returns to the last tab. */
const activeTab = signal<TabId>("controls");

export function SettingsScreen() {
  const panel = useRef<HTMLDivElement>(null);
  const [confirmKeybinds, setConfirmKeybinds] = useState(false);
  useArrowNav(panel);

  const spec = TABS.find((t) => t.id === activeTab.value) ?? TABS[0];
  const Content = spec.Content;

  const reset = () => {
    if (spec.section === null) {
      setConfirmKeybinds(true);
      return;
    }
    resetSection(spec.section);
    toast(`${spec.label} reset to defaults`, "success");
  };

  return (
    <ScreenFrame
      title="Settings"
      kicker="Preferences"
      bodyClass="settings__body"
      footer={
        <span class="settings__footnote">
          <i class="settings__led settings__led--ok" aria-hidden="true" />
          Changes apply instantly
        </span>
      }
    >
      <Tabs
        tabs={TABS}
        active={spec.id}
        idPrefix={ID_PREFIX}
        ariaLabel="Settings sections"
        onChange={(id) => {
          const next = TABS.find((t) => t.id === id);
          if (next) activeTab.value = next.id;
        }}
      />
      <div
        ref={panel}
        class="settings__panel"
        role="tabpanel"
        id={panelId(ID_PREFIX, spec.id)}
        aria-labelledby={tabId(ID_PREFIX, spec.id)}
      >
        <div class="settings__head">
          <p class="settings__lede">{spec.lede}</p>
          <Button size="sm" variant="ghost" onClick={reset}>
            {spec.section === null ? "Reset all keybinds" : "Reset this tab to defaults"}
          </Button>
        </div>
        <div key={spec.id} class="settings__scroll">
          <Content />
        </div>
      </div>

      {confirmKeybinds && (
        <Modal
          title="Reset all keybinds?"
          size="sm"
          onClose={() => setConfirmKeybinds(false)}
          footer={
            <>
              <Button variant="ghost" data-autofocus="" onClick={() => setConfirmKeybinds(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  resetKeybinds();
                  setConfirmKeybinds(false);
                  toast("Keybinds reset to defaults", "success");
                }}
              >
                Reset all
              </Button>
            </>
          }
        >
          <p>Every action goes back to its default keys, mouse buttons and wheel directions.</p>
        </Modal>
      )}
    </ScreenFrame>
  );
}
